import * as React from 'react';
import { useState } from 'react';
import Dropzone from 'react-dropzone';
import config from '../../../../config';
import Loader from '../../../common/Loader/Loader';
import ReactTooltip from 'react-tooltip';
import { QuickTagCreationWizardPageProps } from '../QuickTagCreationWizard';
import TextField from '@mui/material/TextField';
import http from '../../../../utils/http';

enum UPLOAD_STATUS {
    WAITING = 'WAITING',
    UPLOADING = 'UPLOADING',
    VALID = 'VALID'
}

const acceptFormats = {
    'text/csv': ['.csv'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx'
    ],
    'application/x-spss-sav': ['.sav']
};

const MAX_NUM_VERBATIMS = 3000;

/**
 * QuickTagCreationWizard Page for Dataset Upload.
 */
function QuickTagDatasetPage(props: QuickTagCreationWizardPageProps) {
    const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.WAITING);

    const handleUploadError = (error: string) => {
        setUploadStatus(UPLOAD_STATUS.WAITING);
        alert(
            'Could not load data from the uploaded file, please try again or contact support.'
        );
        console.error(error);
    };

    async function upload(acceptedFiles: any): Promise<void> {
        const file = acceptedFiles[0];
        props.setDatasetLabel(file.name);
        try {
            setUploadStatus(UPLOAD_STATUS.UPLOADING);
            const formData = new FormData();
            formData.append('file', file);
            const url = config.api.endpoints.uploadQuickTagFile;
            const response = await http.post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (response?.status !== 200) {
                handleUploadError(
                    `Upload failed with error status: ${response?.status}`
                );
            } else {
                setUploadStatus(UPLOAD_STATUS.VALID);
                if (response.data.hasDuplicateColumnHeaders) {
                    alert(
                        'Warning: your uploaded file has duplicate column headers, which may cause unexpected results.'
                    );
                }
                if (
                    response.data.columnDescriptors.some(
                        (d) => d.hasMissingValues
                    )
                ) {
                    alert(
                        'Warning: some column(s) are missing values in your uploaded file.'
                    );
                }
                props.setDatasetFileType(response.data.fileType);
                props.setDatasetFileUrl(response.data.fileUrl);
                props.setDatasetFileDescriptors(
                    response.data.columnDescriptors
                );
                props.setDatasetHasDuplicateVariables(
                    response.data.hasDuplicateColumnHeaders
                );
                return;
            }
        } catch (e) {
            handleUploadError(e);
        }
    }

    const dropzoneInnerContent =
        uploadStatus === UPLOAD_STATUS.UPLOADING ? (
            <Loader />
        ) : uploadStatus === UPLOAD_STATUS.VALID ? (
            <>
                <i className="nx nx-check text-5xl" />
                <br />
                <span>Looking good!</span>
            </>
        ) : (
            <>
                <i className="nx nx-upload text-5xl" />
                <br />
                <span>Drop your file here or click to browse.</span>
            </>
        );

    return (
        <>
            <Dropzone
                accept={acceptFormats}
                multiple={false}
                onDrop={async (file) => await upload(file)}
            >
                {({ getRootProps, getInputProps }: any) => (
                    <div
                        className="text-blue text-sm text-center cursor-pointer"
                        {...getRootProps()}
                    >
                        <input {...getInputProps()} />
                        <div className="p-4">{dropzoneInnerContent}</div>
                    </div>
                )}
            </Dropzone>
            {Boolean(props.datasetFileUrl) && (
                <TextField
                    fullWidth
                    label="Dataset Label"
                    size="small"
                    value={props.datasetLabel}
                    onChange={(e) => props.setDatasetLabel(e.target.value)}
                    InputLabelProps={{
                        shrink: true
                    }}
                />
            )}
            {uploadStatus !== UPLOAD_STATUS.VALID && (
                <p className="text-sm text-center mt-2">
                    {`(File must include the `}
                    <a
                        data-tip="quickTag-wizard-tooltip-verbatim-dataset"
                        data-for="quickTag-wizard-tooltip-verbatim-dataset"
                        className="quickTag-wizard-tooltip"
                    >
                        verbatims
                    </a>
                    {` to be coded, optionally accompanied by `}
                    <a
                        data-tip="quickTag-wizard-tooltip-unique-ids"
                        data-for="quickTag-wizard-tooltip-unique-ids"
                        className="quickTag-wizard-tooltip"
                    >
                        verbatim IDs
                    </a>{' '}
                    and{' '}
                    <a
                        data-tip="quickTag-wizard-tooltip-language-ids"
                        data-for="quickTag-wizard-tooltip-language-ids"
                        className="quickTag-wizard-tooltip"
                    >
                        language ids
                    </a>
                    {`)`}
                    <ReactTooltip
                        id="quickTag-wizard-tooltip-verbatim-dataset"
                        place="top"
                        getContent={function () {
                            return (
                                <span>
                                    The verbatim dataset is simply the set of
                                    verbatims (or text utterances) that you want
                                    to analyze.
                                    <br />
                                    QuickTag supports up to {
                                        MAX_NUM_VERBATIMS
                                    }{' '}
                                    verbatims in each dataset.
                                    <br />
                                    The dataset may be uploaded as Microsoft
                                    Excel (.xlsx), CSV (.csv), or SPSS (.sav)
                                    file.
                                </span>
                            );
                        }}
                    />
                    <ReactTooltip
                        id="quickTag-wizard-tooltip-unique-ids"
                        place="top"
                        getContent={function () {
                            return (
                                <span>
                                    Verbatim IDs are identifiers uniquely
                                    associated with each verbatim in the
                                    verbatim dataset, which will be retained
                                    during analysis and available in the export.
                                    <br />
                                    You are not required to provide verbatim
                                    IDs: if you do not, then unique ids will be
                                    automatically generated.
                                </span>
                            );
                        }}
                    />
                    <ReactTooltip
                        id={'quickTag-wizard-tooltip-language-ids'}
                        effect="solid"
                        clickable={true}
                        place="top"
                        delayHide={250}
                        getContent={function () {
                            return (
                                <span>
                                    QuickTag can natively handle verbatims in
                                    many languages.
                                    <br />
                                    However, if you would like to use machine
                                    translation to translate verbatims to
                                    English before processing, simply include a{' '}
                                    <b>language id</b> variable/column in your
                                    dataset file.
                                    <br />
                                    The list of{' '}
                                    <a
                                        href="https://cloud.google.com/translate/docs/languages"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        supported language ids is available
                                        here.
                                    </a>{' '}
                                    <br />
                                    Note that providing language ids is
                                    optional: if not provided, no machine
                                    translation will be performed.
                                </span>
                            );
                        }}
                    />
                </p>
            )}
        </>
    );
}

export default QuickTagDatasetPage;
